import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import Portrait from './Portrait'
import type { PlayerSummary } from '../types/protocol'

export interface QueueRow {
  playerId: number
  player: PlayerSummary | undefined
  taken: boolean
}

interface Props {
  rows: QueueRow[]
  busy: boolean
  error: string | null
  onReorder: (playerIds: number[]) => void
  onRemove: (playerId: number) => void
}

// The owner's private queue. Drag to reorder on a desktop; the arrows do the
// same job on a phone, where dragging inside a scrolling list is a fight.
export default function QueuePanel({ rows, busy, error, onReorder, onRemove }: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const ids = rows.map((row) => row.playerId)

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const from = ids.indexOf(Number(active.id))
    const to = ids.indexOf(Number(over.id))
    if (from === -1 || to === -1) return

    onReorder(arrayMove(ids, from, to))
  }

  function move(index: number, delta: number) {
    const to = index + delta
    if (to < 0 || to >= ids.length) return
    onReorder(arrayMove(ids, index, to))
  }

  return (
    <section className="panel p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="eyebrow">My queue</h2>
        <p className="text-[11px] text-ink-lo">Only you can see this</p>
      </div>

      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-ink-lo">
          Queue players from the list below. If your clock runs out, autopick takes the first
          one still available.
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <ol className="mt-3 space-y-1">
              {rows.map((row, index) => (
                <QueueItem
                  key={row.playerId}
                  row={row}
                  index={index}
                  disabled={busy}
                  isFirst={index === 0}
                  isLast={index === rows.length - 1}
                  onMove={move}
                  onRemove={onRemove}
                />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
      )}

      {error && <p className="mt-2 text-sm text-crit">{error}</p>}
    </section>
  )
}

function QueueItem({
  row,
  index,
  disabled,
  isFirst,
  isLast,
  onMove,
  onRemove,
}: {
  row: QueueRow
  index: number
  disabled: boolean
  isFirst: boolean
  isLast: boolean
  onMove: (index: number, delta: number) => void
  onRemove: (playerId: number) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.playerId,
  })

  const style = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  const name = row.player?.full_name ?? `Player #${row.playerId}`

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 rounded-md border border-line bg-surface-2 px-2 py-1.5 shadow-panel"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${name}`}
        className="cursor-grab px-1 text-ink-lo hover:text-ink-mid"
      >
        ⠿
      </button>
      <span className="w-4 shrink-0 text-right font-mono text-[11px] text-ink-lo tabular-nums">
        {index + 1}
      </span>
      {row.player && (
        <Portrait
          fullName={row.player.full_name}
          position={row.player.position}
          nflTeam={row.player.nfl_team}
          src={row.player.portrait_url}
          size="xs"
          className={row.taken ? 'opacity-40' : ''}
        />
      )}
      <span
        className={`min-w-0 flex-1 truncate text-sm ${
          row.taken ? 'text-ink-lo line-through' : 'text-ink-hi'
        }`}
      >
        {name}
        {row.player && (
          <span className="ml-2 font-mono text-[10px] text-ink-lo">
            {row.player.position === 'DEF' ? 'DST' : row.player.position}
            {row.player.nfl_team ? ` · ${row.player.nfl_team}` : ''}
          </span>
        )}
      </span>
      <button
        type="button"
        disabled={disabled || isFirst}
        onClick={() => onMove(index, -1)}
        aria-label={`Move ${name} up`}
        className="px-1 text-ink-lo hover:text-ink-hi disabled:opacity-30"
      >
        ↑
      </button>
      <button
        type="button"
        disabled={disabled || isLast}
        onClick={() => onMove(index, 1)}
        aria-label={`Move ${name} down`}
        className="px-1 text-ink-lo hover:text-ink-hi disabled:opacity-30"
      >
        ↓
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onRemove(row.playerId)}
        aria-label={`Remove ${name} from queue`}
        className="px-1 text-ink-lo hover:text-crit disabled:opacity-30"
      >
        ✕
      </button>
    </li>
  )
}
